import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, MapPin, Crown } from "lucide-react";
import { toast } from "sonner";
import { statusLabel } from "./JobsCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const TimerBadge = ({ timer }) => {
  const running = !!timer?.running;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  const base = timer?.seconds || 0;
  const startedAt = timer?.started_at ? new Date(timer.started_at).getTime() : null;
  const elapsed = base + (running && startedAt ? (Date.now() - startedAt) / 1000 : 0);
  if (!timer || (!running && base === 0)) return <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">No timer yet</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-sm font-bold tabular-nums ${running ? "text-emerald-600" : "text-slate-600"}`}>
      {running && <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />}
      {fmtClock(elapsed)}{running ? " · running" : ""}
    </span>
  );
};

const Row = ({ k, v, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b border-line/50 py-2.5 last:border-b-0" data-testid={testId}>
    <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</span>
    <span className="text-right text-sm text-slate-800">{v || "—"}</span>
  </div>
);

const Section = ({ title, children, testId }) => (
  <div className="mt-6" data-testid={testId}>
    <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-blaze">{title}</p>
    <div className="border border-line bg-slate-100/70 p-5">{children}</div>
  </div>
);

const inputCls =
  "w-full border border-line bg-slate-100/70 px-3 py-2.5 text-sm text-slate-900 placeholder-zinc-500 outline-none focus:border-blaze";
const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500";

const EDIT_FIELDS = [
  ["first_name", "First Name"], ["last_name", "Last Name"], ["mobile", "Mobile"], ["email", "Email"],
  ["pickup_suburb", "Pickup Suburb"], ["pickup_address", "Pickup Address"],
  ["drop_suburb", "Drop-off Suburb"], ["drop_address", "Drop-off Address"],
  ["start_time", "Start Time"], ["bedrooms", "Bedrooms"], ["property_type", "Property Type"],
  ["truck", "Truck"], ["partner_name", "Partner Name"], ["notes", "Notes"],
];
const EDIT_NUMBERS = [["movers", "Movers"], ["total_hours", "Est. Hours"], ["estimated_price", "Price ($)"], ["rate", "Rate ($/hr)"]];

export const JobDetailModal = ({ jobId, onClose, isOwner = true }) => {
  const [data, setData] = useState(null);
  const [hoursEdit, setHoursEdit] = useState({});
  const [savingHours, setSavingHours] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [callout, setCallout] = useState({});
  const [savingCallout, setSavingCallout] = useState(false);
  const [allMovers, setAllMovers] = useState([]);
  const [assignList, setAssignList] = useState([]);
  const [savingAssign, setSavingAssign] = useState(false);
  const [allContractors, setAllContractors] = useState([]);
  const [contractorId, setContractorId] = useState("");
  const [savingContractor, setSavingContractor] = useState(false);
  const [chargeAmt, setChargeAmt] = useState("");
  const [inv, setInv] = useState({ hours: "", rate: "", callout: "" });
  const [invSending, setInvSending] = useState(false);
  const [invExtrasOn, setInvExtrasOn] = useState(false);
  const [invExtras, setInvExtras] = useState([{ reason: "Stairs", amount: "" }]);
  const [invMethod, setInvMethod] = useState("card");
  const [resendPrompt, setResendPrompt] = useState(null);
  const [resendJob, setResendJob] = useState(true);
  const [resendInv, setResendInv] = useState(true);
  const [resending, setResending] = useState(false);

  const setInvExtra = (i, k, v) => setInvExtras((xs) => xs.map((e, j) => (j === i ? { ...e, [k]: v } : e)));

  const sendInvoice = async () => {
    const hours = parseFloat(inv.hours);
    const rate = parseFloat(inv.rate);
    if (!Number.isFinite(hours) || hours <= 0 || !Number.isFinite(rate) || rate <= 0) {
      return toast.error("Enter valid hours and hourly rate for the invoice.");
    }
    const extras = invExtrasOn
      ? invExtras
          .filter((e) => parseFloat(e.amount) > 0 && (e.reason || "").trim())
          .map((e) => ({ reason: e.reason.trim(), amount: parseFloat(e.amount) }))
      : [];
    setInvSending(true);
    try {
      const { data: res } = await axios.post(`${API}/jobs/${jobId}/invoice`, {
        hours,
        rate,
        callout_minutes: parseFloat(inv.callout) || 0,
        extras,
        payment_method: invMethod,
      });
      const methodLabel = invMethod === "card" ? "pay by card" : invMethod === "bank" ? "bank transfer" : "cash on the day";
      toast.success(`Invoice for $${res.invoice.due_total ?? res.invoice.total} sent to ${res.sent_to} (${methodLabel}).`);
      reload();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not send invoice.");
    } finally {
      setInvSending(false);
    }
  };

  const doResend = async () => {
    setResending(true);
    try {
      const tasks = [];
      if (resendJob) tasks.push(axios.post(`${API}/bookings/${jobId}/resend-confirmation`));
      if (resendInv && resendPrompt?.hasInvoice) tasks.push(axios.post(`${API}/jobs/${jobId}/invoice/resend`));
      if (tasks.length === 0) { setResendPrompt(null); return; }
      await Promise.all(tasks);
      toast.success(`Re-sent to ${resendPrompt.email}`);
      setResendPrompt(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend notifications.");
    } finally {
      setResending(false);
    }
  };
  const [chargeNote, setChargeNote] = useState("");
  const [charging, setCharging] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const cancelBooking = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/bookings/${jobId}/cancel`, { reason: cancelReason.trim() });
      toast.success("Booking cancelled — customer has been emailed.");
      setCancelMode(false);
      setCancelReason("");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel booking.");
    } finally {
      setCancelling(false);
    }
  };

  const reload = () =>
    axios.get(`${API}/jobs/${jobId}/detail`).then(({ data }) => {
      setData(data);
      setHoursEdit({ ...(data.booking?.work_hours || {}), ...(data.completion?.mover_hours || {}) });
      setCallout(data.booking?.callout_pay || {});
      setAssignList(data.booking?.assigned_movers || []);
      setContractorId(data.booking?.contractor_id || "");
    }).catch(() => setData(false));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reload();
    axios.get(`${API}/movers`).then(({ data }) => setAllMovers(data)).catch(() => {});
    if (isOwner) axios.get(`${API}/contractors`).then(({ data }) => setAllContractors(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const chargeSavedCard = async () => {
    const amt = parseFloat(chargeAmt);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid amount.");
    setCharging(true);
    try {
      const { data: p } = await axios.post(`${API}/jobs/${jobId}/charge-saved-card`, { amount: amt, note: chargeNote.trim() });
      toast.success(`Charged $${p.total} to the saved card. Invoice emailed to the customer.`);
      setChargeAmt("");
      setChargeNote("");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Charge failed.");
    } finally {
      setCharging(false);
    }
  };

  const toggleAssign = (name) =>
    setAssignList((l) => (l.includes(name) ? l.filter((m) => m !== name) : [...l, name]));

  const [silentAssign, setSilentAssign] = useState(false);

  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await axios.post(`${API}/bookings/${jobId}/assign`, { movers: assignList, silent: silentAssign });
      setData((d) => ({ ...d, booking: { ...d.booking, assigned_movers: assignList, assigned_at: new Date().toISOString() } }));
      toast.success(silentAssign ? "Assignment saved silently — no emails sent to movers." : "Assignment saved — new movers emailed, removed movers notified.");
    } catch {
      toast.error("Could not update assignment.");
    } finally {
      setSavingAssign(false);
    }
  };

  const saveContractor = async () => {
    setSavingContractor(true);
    try {
      const { data: res } = await axios.patch(`${API}/bookings/${jobId}/assign-contractor`, { contractor_id: contractorId || null });
      setData((d) => ({ ...d, booking: { ...d.booking, contractor_id: res.contractor_id || "", contractor_name: res.contractor_name || "" } }));
      toast.success(res.contractor_id ? `Job assigned to ${res.contractor_name} — contractor notified.` : "Contractor removed from this job.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update contractor.");
    } finally {
      setSavingContractor(false);
    }
  };

  const saveCallout = async () => {
    setSavingCallout(true);
    try {
      const cp = {};
      Object.entries(callout).forEach(([k, v]) => {
        const n = parseFloat(v);
        if (Number.isFinite(n) && n > 0) cp[k] = n;
      });
      await axios.put(`${API}/bookings/${jobId}`, { callout_pay: cp });
      setData((d) => ({ ...d, booking: { ...d.booking, callout_pay: cp } }));
      toast.success("Call-out pay saved.");
    } catch {
      toast.error("Could not save call-out pay.");
    } finally {
      setSavingCallout(false);
    }
  };

  const startEdit = () => {
    const b = data.booking;
    setEditForm({
      first_name: b.first_name || "", last_name: b.last_name || "", mobile: b.mobile || "", email: b.email || "",
      pickup_suburb: b.pickup_suburb || "", pickup_address: b.pickup_address || "",
      drop_suburb: b.drop_suburb || "", drop_address: b.drop_address || "",
      moving_date: b.moving_date || "", start_time: b.start_time || "",
      bedrooms: b.bedrooms || "", property_type: b.property_type || "", truck: b.truck || "",
      movers: b.movers ?? 2, total_hours: b.total_hours ?? 0, estimated_price: b.estimated_price ?? 0,
      rate: b.rate ?? 0, notes: b.notes || "", partner_name: b.partner_name || "", status: b.status || "booked", payment_status: b.payment_status || "unpaid",
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const prevEmail = (data.booking.email || "").trim().toLowerCase();
      const hadInvoice = !!data.booking.invoice;
      const payload = { ...editForm };
      ["movers", "total_hours", "estimated_price", "rate"].forEach((k) => { payload[k] = parseFloat(payload[k]) || 0; });
      payload.movers = parseInt(payload.movers) || 1;
      await axios.put(`${API}/bookings/${jobId}`, payload);
      setData((d) => ({ ...d, booking: { ...d.booking, ...payload } }));
      setEditMode(false);
      toast.success("Job updated.");
      const newEmail = (editForm.email || "").trim().toLowerCase();
      if (newEmail && newEmail !== prevEmail) {
        setResendJob(true);
        setResendInv(hadInvoice);
        setResendPrompt({ email: editForm.email.trim(), hasInvoice: hadInvoice });
      }
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      const mh = {};
      Object.entries(hoursEdit).forEach(([k, v]) => { mh[k] = parseFloat(v) || 0; });
      await axios.post(`${API}/jobs/${jobId}/hours`, { mover_hours: mh });
      setData((d) => ({ ...d, completion: { ...d.completion, mover_hours: mh } }));
      toast.success("Hours updated.");
    } catch {
      toast.error("Could not save hours.");
    } finally {
      setSavingHours(false);
    }
  };

  const setPayment = async (v) => {
    try {
      await axios.patch(`${API}/bookings/${jobId}`, { payment_status: v });
      setData((d) => ({ ...d, booking: { ...d.booking, payment_status: v } }));
      toast.success(`Payment marked ${v}`);
    } catch {
      toast.error("Could not update payment status.");
    }
  };

  const b = data?.booking;
  const c = data?.completion;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-5" onClick={onClose} data-testid="job-detail-modal">
      {resendPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-5" onClick={(e) => e.stopPropagation()} data-testid="resend-prompt-overlay">
          <div className="w-full max-w-md border border-line bg-coal p-6">
            <p className="font-display text-lg font-semibold uppercase tracking-tight text-slate-900">Email changed</p>
            <p className="mt-1 text-sm text-slate-600">The customer email was updated to <strong className="text-slate-900">{resendPrompt.email}</strong>. Resend notifications to the new address?</p>
            <div className="mt-4 space-y-3">
              <label className="flex items-center gap-3 text-sm font-semibold text-slate-800" data-testid="resend-job-toggle">
                <input type="checkbox" checked={resendJob} onChange={(e) => setResendJob(e.target.checked)} className="h-4 w-4 accent-blaze" />
                Resend booking confirmation (job details)
              </label>
              <label className={`flex items-center gap-3 text-sm font-semibold ${resendPrompt.hasInvoice ? "text-slate-800" : "text-slate-400"}`} data-testid="resend-invoice-toggle">
                <input type="checkbox" checked={resendInv && resendPrompt.hasInvoice} disabled={!resendPrompt.hasInvoice} onChange={(e) => setResendInv(e.target.checked)} className="h-4 w-4 accent-blaze" />
                Resend latest invoice {resendPrompt.hasInvoice ? "" : "(none sent yet)"}
              </label>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setResendPrompt(null)} className="flex-1 border border-line py-2.5 text-xs font-bold uppercase tracking-widest text-slate-600" data-testid="resend-skip-btn">Skip</button>
              <button onClick={doResend} disabled={resending || (!resendJob && !(resendInv && resendPrompt.hasInvoice))} className="flex-1 bg-blaze py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="resend-send-btn">{resending ? "Sending…" : "Resend now"}</button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto my-8 w-full max-w-2xl border border-line bg-coal p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">
              Job <span className="text-blaze">{jobId}</span>
            </h3>
            {b && (() => {
              const partner = (b.source === "movers_buddy") || (jobId || "").startsWith("MB-") || !!b.ccrm_id;
              return (
                <span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest ${partner ? "bg-slate-200 text-slate-600" : "bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-sm"}`} data-testid="jobdetail-source">
                  {partner ? "Movers Buddy" : <><Crown className="h-3 w-3" /> MoversNearby</>}
                </span>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            {b && !editMode && (
              <button onClick={startEdit} className="border border-blaze px-4 py-2 text-xs font-bold uppercase tracking-widest text-blaze transition-colors hover:bg-blaze hover:text-white" data-testid="edit-job-btn">
                Edit Job
              </button>
            )}
            {b && !editMode && b.status !== "cancelled" && (
              <button onClick={() => setCancelMode(true)} className="border border-red-500 px-4 py-2 text-xs font-bold uppercase tracking-widest text-red-600 transition-colors hover:bg-red-600 hover:text-white" data-testid="cancel-job-btn">
                Cancel Booking
              </button>
            )}
            <button onClick={onClose} className="p-1 text-slate-600 hover:text-slate-900" data-testid="job-detail-close"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {cancelMode && (
          <div className="mt-6 border border-red-300 bg-red-50 p-5" data-testid="cancel-booking-box">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600">Cancel this booking?</p>
            <p className="mt-1 text-xs text-slate-600">The customer will receive a cancellation email{b?.booking_fee_paid > 0 ? ` — their paid $${b.booking_fee_paid} booking fee is mentioned for refund` : ""}.</p>
            <label className={`${labelCls} mt-4`}>Reason for cancellation</label>
            <textarea rows={2} className={inputCls} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Customer postponed the move / Customer cancelled by phone" data-testid="cancel-reason-input" />
            <div className="mt-3 flex gap-2">
              <button onClick={cancelBooking} disabled={cancelling} className="bg-red-600 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="cancel-confirm-btn">
                {cancelling ? "Cancelling…" : "Confirm Cancellation"}
              </button>
              <button onClick={() => setCancelMode(false)} className="border border-line px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-slate-600" data-testid="cancel-back-btn">
                Keep Booking
              </button>
            </div>
          </div>
        )}

        {b?.status === "cancelled" && (
          <div className="mt-6 border border-red-300 bg-red-50 p-5" data-testid="cancelled-note">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600">Booking Cancelled</p>
            <p className="mt-1 text-sm text-slate-700">{b.cancel_reason || "—"} {b.cancelled_at ? `— ${b.cancelled_at}` : ""}</p>
          </div>
        )}

        {editMode && (
          <div className="mt-6" data-testid="edit-job-form">
            <div className="grid gap-4 sm:grid-cols-2">
              {EDIT_FIELDS.slice(0, 4).map(([k, l]) => (
                <div key={k}><label className={labelCls}>{l}</label><input className={inputCls} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`edit-${k}`} /></div>
              ))}
              <div><label className={labelCls}>Moving Date</label><input type="date" className={`${inputCls} text-slate-700`} value={editForm.moving_date} onChange={(e) => setEditForm({ ...editForm, moving_date: e.target.value })} data-testid="edit-moving_date" /></div>
              {EDIT_FIELDS.slice(4).map(([k, l]) => (
                <div key={k}><label className={labelCls}>{l}</label><input className={inputCls} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`edit-${k}`} /></div>
              ))}
              {EDIT_NUMBERS.map(([k, l]) => (
                <div key={k}><label className={labelCls}>{l}</label><input type="number" step="0.5" className={inputCls} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`edit-${k}`} /></div>
              ))}
              <div>
                <label className={labelCls}>Job Status</label>
                <select className={inputCls} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} data-testid="edit-status">
                  {["booked", "confirmed", "in_progress", "completed", "cancelled"].map((s) => <option key={s} value={s} className="bg-coal text-slate-900">{s.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Payment Status</label>
                <select className={inputCls} value={editForm.payment_status} onChange={(e) => setEditForm({ ...editForm, payment_status: e.target.value })} data-testid="edit-payment_status">
                  <option value="unpaid" className="bg-coal text-slate-900">Unpaid</option>
                  <option value="paid" className="bg-coal text-slate-900">Paid</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={saveEdit} disabled={savingEdit} className="flex items-center gap-2 bg-blaze px-8 py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="save-job-edit-btn">
                {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </button>
              <button onClick={() => setEditMode(false)} className="border border-line px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-600 hover:border-white hover:text-slate-900" data-testid="cancel-job-edit-btn">
                Cancel
              </button>
            </div>
          </div>
        )}

        {!data && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>}
        {data === false && <p className="py-10 text-center text-sm text-red-600">Could not load job details.</p>}

        {b && !editMode && (
          <>
            <Section title="Customer">
              <Row k="Name" v={`${b.first_name} ${b.last_name}`} />
              <Row k="Email" v={b.email} />
              <Row k="Phone" v={b.mobile} />
            </Section>
            <Section title="Job">
              <Row k="Quote #" v={b.quote_number} />
              <Row k="Booking #" v={b.job_id} />
              <Row k="Date" v={`${b.moving_date} — ${b.start_time}`} />
              <Row k="Pickup" v={[b.pickup_address, b.pickup_suburb].filter(Boolean).join(", ")} />
              <Row k="Drop-off" v={[b.drop_address, b.drop_suburb].filter(Boolean).join(", ")} />
              {(b.bedrooms || b.property_type) && <Row k="Home" v={[b.bedrooms ? `${b.bedrooms} bedroom` : "", b.property_type].filter(Boolean).join(" ")} />}
              <Row k="Truck" v={b.truck} />
              <Row k="Contractor" v={b.contractor_name || "Unassigned"} />
              <Row k="Movers Assigned" v={(b.assigned_movers || []).length ? b.assigned_movers.join(", ") : "Unassigned"} />
              {b.crew_status && Object.keys(b.crew_status).length > 0 && (
                <Row k="Live Crew Status" v={Object.entries(b.crew_status).map(([n, s]) => `${n}: ${s.status === "arrived" ? "Arrived" : "On the way"} ${s.at}`).join(" · ")} />
              )}
              {b.crew_location && Object.keys(b.crew_location).length > 0 && (
                <div className="flex flex-wrap gap-1.5 py-2" data-testid="detail-crew-locations">
                  {Object.entries(b.crew_location).map(([n, l]) => (
                    <a
                      key={n}
                      href={`https://www.google.com/maps?q=${l.lat},${l.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1.5 text-[11px] font-bold text-blaze underline-offset-2 hover:underline"
                    >
                      <MapPin className="h-3 w-3" /> {n}'s live location · {l.at}
                    </a>
                  ))}
                </div>
              )}
              {b.partner_name && <Row k="Partner" v={b.partner_name} />}
              <Row k="Status" v={statusLabel(b, !isOwner).label} />
              {b.notes && <Row k="Notes" v={b.notes} />}
            </Section>
            <Section title={isOwner ? "Contractor Assignment" : "Crew Assignment"}>
              {isOwner ? (
                <>
                  <p className="mb-3 text-xs text-slate-500">Assign this job to a contractor — they get notified immediately. Click again to unselect, then save to remove.</p>
                  <div className="flex flex-wrap gap-2" data-testid="detail-contractor-options">
                    {allContractors.map((c) => {
                      const active = contractorId === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setContractorId((v) => (v === c.id ? "" : c.id))}
                          data-testid={`detail-contractor-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`border px-4 py-2 text-xs font-semibold transition-all ${active ? "border-blaze bg-blaze text-white" : "border-line text-slate-700 hover:border-blaze/60"}`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                    {allContractors.length === 0 && <p className="text-xs text-slate-500">No contractors yet — add one from the Contractors tab.</p>}
                  </div>
                  <button
                    onClick={saveContractor}
                    disabled={savingContractor}
                    className="mt-4 flex items-center gap-2 bg-blaze px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                    data-testid="save-contractor-btn"
                  >
                    {savingContractor ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Contractor"}
                  </button>
                </>
              ) : (
                <>
                  <p className="mb-3 text-xs text-slate-500">Tick movers to assign. Untick to remove — new movers get a job email, removed movers get a cancellation email.</p>
                  <div className="flex flex-wrap gap-2" data-testid="detail-assign-options">
                    {allMovers.map((m) => {
                      const active = assignList.includes(m.name);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleAssign(m.name)}
                          data-testid={`detail-assign-${m.name.toLowerCase()}`}
                          className={`border px-4 py-2 text-xs font-semibold transition-all ${active ? "border-blaze bg-blaze text-white" : "border-line text-slate-700 hover:border-blaze/60"}`}
                        >
                          {m.name}
                        </button>
                      );
                    })}
                  </div>
                  <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-xs font-semibold text-slate-700" data-testid="silent-assign-label">
                    <input
                      type="checkbox"
                      checked={silentAssign}
                      onChange={(e) => setSilentAssign(e.target.checked)}
                      className="h-4 w-4 accent-blue-600"
                      data-testid="silent-assign-checkbox"
                    />
                    Assign silently — don't send any emails/notifications to movers
                  </label>
                  <button
                    onClick={saveAssign}
                    disabled={savingAssign}
                    className="mt-4 flex items-center gap-2 bg-blaze px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                    data-testid="save-assign-btn"
                  >
                    {savingAssign ? <Loader2 className="h-4 w-4 animate-spin" /> : silentAssign ? "Save Silently" : "Save Assignment"}
                  </button>
                </>
              )}
            </Section>
            {(b.assigned_movers || []).length > 0 && (
              <Section title="Work Hours & Timer" testId="work-hours-section">
                <p className="mb-3 text-xs text-slate-500">Live timers from the movers' app. Hours feed straight into pay — adjust any value and save.</p>
                <div className="space-y-2.5">
                  {(b.assigned_movers || []).map((m) => (
                    <div key={m} className="flex items-center justify-between gap-4 border-b border-line/50 pb-2.5 last:border-b-0" data-testid={`work-hours-row-${m.toLowerCase()}`}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{m}</p>
                        <TimerBadge timer={(b.timers || {})[m]} />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="0" step="0.01"
                          value={hoursEdit[m] ?? ""}
                          onChange={(e) => setHoursEdit({ ...hoursEdit, [m]: e.target.value })}
                          placeholder="0"
                          className="w-24 border border-line bg-ink px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-blaze"
                          data-testid={`edit-hours-${m.toLowerCase()}`}
                        />
                        <span className="text-xs text-slate-500">hrs</span>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={saveHours}
                    disabled={savingHours}
                    className="mt-2 bg-blaze px-5 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                    data-testid="save-hours-admin-btn"
                  >
                    {savingHours ? "Saving…" : "Save Hours"}
                  </button>
                </div>
              </Section>
            )}
            {b.start_signature_id && (
              <Section title="Job Start Sign-Off" testId="start-signoff-section">
                <Row k="Started" v={b.start_signed_at ? new Date(b.start_signed_at).toLocaleString("en-AU", { timeZone: "Australia/Melbourne" }) : "—"} />
                <Row k="Signed by" v={b.start_signed_by || `${b.first_name || ""} ${b.last_name || ""}`.trim() || "Customer"} />
                <div className="mt-3">
                  <p className="mb-2 text-[10px] uppercase tracking-widest text-slate-500">Customer start signature</p>
                  <img src={`${API}/signatures/${b.start_signature_id}`} alt="Customer start signature" className="w-full max-w-[260px] border border-line bg-white" data-testid="detail-start-sig" />
                </div>
              </Section>
            )}
            {(b.inventory || []).length > 0 && (
              <Section title="Items">
                <div className="flex flex-wrap gap-2">
                  {b.inventory.map((i) => <span key={i} className="border border-line px-2.5 py-1 text-xs text-slate-700">{i}</span>)}
                </div>
              </Section>
            )}
            {isOwner && (
            <Section title="Price & Payment">
              <Row k="Rate" v={`$${b.rate}/hr`} />
              <Row k="Estimated" v={`${b.total_hours ?? "—"} hrs${b.estimated_price ? ` — $${b.estimated_price}` : ""}`} />
              {b.booking_fee ? <Row k="Booking Fee" v={b.booking_fee_paid >= b.booking_fee ? `$${b.booking_fee} — Paid` : `$${b.booking_fee} — ${b.booking_fee_paid ? `$${b.booking_fee_paid} paid, balance pending` : "Awaiting payment"}`} /> : null}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Payment Status</span>
                <select
                  value={b.payment_status || "unpaid"}
                  onChange={(e) => setPayment(e.target.value)}
                  className="cursor-pointer border border-line bg-ink px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-800 outline-none"
                  data-testid="payment-status-select"
                >
                  <option value="unpaid" className="bg-coal text-slate-900">Unpaid</option>
                  <option value="paid" className="bg-coal text-slate-900">Paid</option>
                </select>
              </div>
              {(b.assigned_movers || []).length > 0 && (
                <div className="mt-4 border-t border-line/50 pt-4">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Call-Out Pay (per mover, $)</p>
                  <div className="space-y-2">
                    {(b.assigned_movers || []).map((m) => (
                      <div key={m} className="flex items-center justify-between gap-4">
                        <span className="text-sm text-slate-700">{m}</span>
                        <input
                          type="number" min="0" step="1" placeholder="0"
                          value={callout[m] ?? ""}
                          onChange={(e) => setCallout({ ...callout, [m]: e.target.value })}
                          className="w-24 border border-line bg-ink px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-blaze"
                          data-testid={`callout-${m.toLowerCase()}`}
                        />
                      </div>
                    ))}
                    <button
                      onClick={saveCallout}
                      disabled={savingCallout}
                      className="mt-1 bg-blaze px-5 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                      data-testid="save-callout-btn"
                    >
                      {savingCallout ? "Saving…" : "Save Call-Out Pay"}
                    </button>
                  </div>
                </div>
              )}
            </Section>
            )}
            <Section title="Sign-Off">
              {c ? (
                <>
                  <Row k="Signed Off By" v={c.mover_name} />
                  <Row k="Customer" v={c.customer_name} />
                  <Row k="Completed At" v={(c.completed_at || "").slice(0, 16).replace("T", " ")} />
                  <Row k="Damage" v={c.report_damage ? `Reported — ${c.damage_description || ""}` : "No new damage"} />
                  {c.actual_hours != null && <Row k="Actual Hours Worked" v={`${c.actual_hours} hrs`} />}
                  {c.travel_hours != null && <Row k="Travel Hours" v={`${c.travel_hours} hrs`} />}
                  {isOwner && c.extra_payment != null && <Row k="Extra Payment Collected" v={`$${c.extra_payment}`} />}
                  {c.notes && <Row k="Mover Notes" v={c.notes} />}
                  {(c.customer_signature_id || c.mover_signature_id) && (
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      {c.mover_signature_id && (
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-widest text-slate-500">Mover Signature</p>
                          <img src={`${API}/signatures/${c.mover_signature_id}`} alt="Mover signature" className="border border-line bg-white" data-testid="detail-mover-sig" />
                        </div>
                      )}
                      {c.customer_signature_id && (
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-widest text-slate-500">Customer Signature</p>
                          <img src={`${API}/signatures/${c.customer_signature_id}`} alt="Customer signature" className="border border-line bg-white" data-testid="detail-customer-sig" />
                        </div>
                      )}
                    </div>
                  )}
                  {((c.photos || []).length > 0 || c.damage_photo) && (
                    <div className="mt-4">
                      <p className="mb-2 text-[10px] uppercase tracking-widest text-slate-500">Photos</p>
                      <div className="grid grid-cols-3 gap-2">
                        {(c.photos || []).map((p, i) => <img key={i} src={p} alt={`Job photo ${i + 1}`} className="border border-line object-cover" data-testid={`detail-photo-${i}`} />)}
                        {c.damage_photo && <img src={c.damage_photo} alt="Damage" className="border border-red-500/40 object-cover" data-testid="detail-damage-photo" />}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">Not signed off yet.</p>
              )}
            </Section>
            {isOwner && (
            <Section title="Payment">
              <div className="mb-4 border border-blaze/40 bg-blue-50/40 p-4" data-testid="send-invoice-box">
                <p className="text-[10px] font-bold uppercase tracking-widest text-blaze">Send Invoice To Customer</p>
                <p className="mt-1 text-[10px] text-slate-500">
                  Choose how the customer pays — the invoice email shows only that option. A paid invoice is sent automatically once payment is received.
                  {b.invoice && <span className="ml-1 font-bold text-green-700">Last sent: ${b.invoice.due_total ?? b.invoice.total} on {new Date(b.invoice.sent_at).toLocaleDateString("en-AU")}</span>}
                </p>
                <div className="mt-3 flex items-center gap-2" data-testid="invoice-method-selector">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Pay by</span>
                  {[
                    { k: "card", label: "Card" },
                    { k: "bank", label: "Bank Transfer" },
                    { k: "cash", label: "Cash" },
                  ].map((m) => (
                    <button
                      key={m.k}
                      type="button"
                      onClick={() => setInvMethod(m.k)}
                      className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${invMethod === m.k ? "border-blaze bg-blaze text-white" : "border-zinc-300 text-slate-700"}`}
                      data-testid={`invoice-method-${m.k}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                  <input type="number" min="0" step="0.5" placeholder={`Hours${b.total_hours ? ` (est ${b.total_hours})` : ""}`} className={inputCls} value={inv.hours} onChange={(e) => setInv({ ...inv, hours: e.target.value })} data-testid="invoice-hours" />
                  <input type="number" min="0" step="1" placeholder={`Rate $/hr${b.rate ? ` (${b.rate})` : ""}`} className={inputCls} value={inv.rate} onChange={(e) => setInv({ ...inv, rate: e.target.value })} data-testid="invoice-rate" />
                  <input type="number" min="0" step="5" placeholder="Call-out mins" className={inputCls} value={inv.callout} onChange={(e) => setInv({ ...inv, callout: e.target.value })} data-testid="invoice-callout" />
                  <button onClick={sendInvoice} disabled={invSending} className="bg-blaze px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="send-invoice-btn">
                    {invSending ? "Sending…" : "Send Invoice"}
                  </button>
                </div>
                <div className="mt-3 border-t border-blaze/20 pt-3" data-testid="invoice-extras-section">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Extra charges?</span>
                    <button type="button" onClick={() => setInvExtrasOn(true)} className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${invExtrasOn ? "border-blaze bg-blaze text-white" : "border-zinc-300 text-slate-700"}`} data-testid="invoice-extras-yes">Yes</button>
                    <button type="button" onClick={() => setInvExtrasOn(false)} className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${!invExtrasOn ? "border-blaze bg-blaze text-white" : "border-zinc-300 text-slate-700"}`} data-testid="invoice-extras-no">No</button>
                  </div>
                  {invExtrasOn && (
                    <div className="mt-3 space-y-2" data-testid="invoice-extras-list">
                      {invExtras.map((e, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input type="text" placeholder="Reason (e.g. Stairs, Fuel)" className={`${inputCls} flex-1`} value={e.reason} onChange={(ev) => setInvExtra(i, "reason", ev.target.value)} data-testid={`invoice-extra-reason-${i}`} />
                          <input type="number" min="0" step="1" placeholder="$" className={`${inputCls} w-28`} value={e.amount} onChange={(ev) => setInvExtra(i, "amount", ev.target.value)} data-testid={`invoice-extra-amount-${i}`} />
                          {invExtras.length > 1 && (
                            <button type="button" onClick={() => setInvExtras((xs) => xs.filter((_, j) => j !== i))} className="text-xs font-bold text-slate-400 hover:text-blaze" data-testid={`invoice-extra-remove-${i}`}>✕</button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={() => setInvExtras((xs) => [...xs, { reason: "", amount: "" }])} className="text-[10px] font-bold uppercase tracking-widest text-blaze" data-testid="invoice-extra-add">+ Add another charge</button>
                    </div>
                  )}
                </div>
              </div>
              {b.saved_card && (
                <div className="mb-4 border border-line/60 bg-white p-4" data-testid="saved-card-box">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Card on file — {b.saved_card.brand} •••• {b.saved_card.last4}
                  </p>
                  <p className="mt-1 text-[10px] text-slate-400">Saved with the customer's consent at payment. Use if the customer hasn't paid.</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                    <input type="number" min="0" step="1" placeholder="Amount $" className={inputCls} value={chargeAmt} onChange={(e) => setChargeAmt(e.target.value)} data-testid="charge-amount" />
                    <input placeholder="Reason (e.g. unpaid balance)" className={inputCls} value={chargeNote} onChange={(e) => setChargeNote(e.target.value)} data-testid="charge-note" />
                    <button
                      onClick={chargeSavedCard}
                      disabled={charging}
                      className="bg-blaze px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                      data-testid="charge-card-btn"
                    >
                      {charging ? "Charging…" : "Charge Saved Card"}
                    </button>
                  </div>
                </div>
              )}
              {data.payment ? (
                <>
                  <Row k="Status" v={data.payment.status === "skipped" ? "Payment Skipped — $0 collected" : data.payment.status === "collected" ? "Collected" : "Pending"} />
                  <Row k="Method" v={{ card: "Card", bank: "PayID / Bank Transfer", cash: "Cash", skipped: "—" }[data.payment.method] || data.payment.method} />
                  {data.payment.status !== "skipped" && (
                    <>
                      <Row k="Hours" v={`${data.payment.hours} hrs`} />
                      <Row k="Call-out" v={`${data.payment.callout_minutes} min`} />
                      <Row k="Rate" v={`$${data.payment.rate}/hr`} />
                      <Row k="Job Total" v={`$${data.payment.subtotal}`} />
                      {(data.payment.extras || []).map((e, i) => (
                        <Row key={i} k={`Extra — ${e.reason}`} v={`$${e.amount}`} />
                      ))}
                      {data.payment.extras_total ? <Row k="Total Job Amount" v={`$${data.payment.total_job}`} /> : null}
                      {data.payment.booking_fee_paid ? <Row k="Booking Fee Already Paid" v={`-$${data.payment.booking_fee_paid}`} /> : null}
                      {data.payment.surcharge ? <Row k="Card Surcharge (2.5%)" v={`$${data.payment.surcharge}`} /> : null}
                      <Row k="Final Amount Collected" v={`$${data.payment.total}`} />
                    </>
                  )}
                  {data.payment.transaction_ref && <Row k="Transaction Ref" v={data.payment.transaction_ref} />}
                  <Row k="Collected By" v={data.payment.collected_by} />
                  <Row k="Date / Time" v={data.payment.paid_at} />
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-500">No payment recorded for this job yet.</p>
                  <a
                    href={`/signoff?job=${b.job_id}`}
                    className="mt-3 inline-block bg-blaze px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white"
                    data-testid="take-payment-link"
                  >
                    Take Payment / Sign Off
                  </a>
                </>
              )}
            </Section>
            )}
            {isOwner ? (
            <Section title="Job Financials" testId="job-financials">
              <Row k="Received from Customer" v={`$${data.financials?.received ?? 0}`} testId="fin-received" />
              {(data.financials?.movers || []).map((m, i) => (
                <Row key={i} k={`Mover — ${m.mover}`} v={`${m.hours} hrs × $${m.rate}/hr${m.callout ? ` + $${m.callout} call-out` : ""} = $${m.pay}`} testId={`fin-mover-${m.mover.toLowerCase()}`} />
              ))}
              <Row k="Mover Payout" v={`$${data.financials?.mover_payout ?? 0}`} testId="fin-payout" />
              <Row k="Margin" v={`${(data.financials?.margin ?? 0) < 0 ? "−" : ""}$${Math.abs(data.financials?.margin ?? 0)}`} testId="fin-margin" />
              {data.financials?.contractor_share != null && (
                <Row k={`Contractor Share — ${data.financials.contractor_name || "Contractor"} (${data.financials.contractor_share_percent}%)`} v={`$${data.financials?.contractor_share ?? 0}`} testId="fin-contractor-share" />
              )}
            </Section>
            ) : (
            <Section title="My Earnings" testId="my-earnings">
              <p className="mb-3 text-xs text-slate-500">What you earn on this job — your share plus what your movers are owed. Customer payments are handled by the office.</p>
              {(data.contractor_earnings?.movers || []).length > 0 && (
                <>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Mover pay (you pay your crew)</p>
                  {(data.contractor_earnings.movers).map((m, i) => (
                    <Row key={i} k={m.mover} v={`${m.hours} hrs × $${m.rate}/hr${m.callout ? ` + $${m.callout} call-out` : ""} = $${m.pay}`} testId={`earn-mover-${m.mover.toLowerCase()}`} />
                  ))}
                  <Row k="Total mover pay" v={`$${data.contractor_earnings?.mover_payout ?? 0}`} testId="earn-mover-payout" />
                </>
              )}
              <div className="mt-3 border-t border-line/50 pt-3">
                {data.contractor_earnings?.completed ? (
                  <>
                    <Row k={`Your share (${data.contractor_earnings?.share_percent ?? 0}% of labour + 100% extras)`} v={`$${data.contractor_earnings?.share_amount ?? 0}`} testId="earn-share" />
                    {(data.contractor_earnings?.extras_total ?? 0) > 0 && <p className="mt-2 text-[11px] text-slate-400">Includes ${data.contractor_earnings.extras_total} extras (paid to you 100%)</p>}
                  </>
                ) : (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="earn-pending">Your share shows here once the job is completed and signed off.</p>
                )}
              </div>
            </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};
